import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { AsyncSection, EmptyState, PageHeader, Panel, useNotifications } from '../../components';
import { PATHS, staffCompanyPath } from '../../routes/paths';
import { useCompanies, useCreateContract, useInsurers } from '../queries';
import { ContractForm } from './ContractForm';
import {
  EMPTY_CONTRACT_FORM,
  ONBOARDING_COPY,
  contractCreateBody,
  type ContractFormState,
} from './onboarding';

/**
 * Adding a contract to one company.
 *
 * A route under the company rather than a panel permanently open at the bottom
 * of its detail view, for the reason every other registry on this side of the
 * product now follows: a screen reached from a link answers one question, and a
 * form sitting below the answer is an invitation to type into it while reading.
 *
 * It stays *under the company* rather than standing on its own, and that part
 * is structural: `POST /contracts` resolves the company from the `X-Company-Id`
 * scope header and not from the body, so the company is not a field and cannot
 * be one. The uuid in the address is what the header is built from -- which is
 * also why this screen checks that the uuid names a company it can see before
 * offering the form, instead of posting into a scope nobody chose.
 *
 * On success it returns to the company's Contratos tab, whose list refetches.
 * Leaving without saving is a link back to the same place and creates nothing.
 */
export function NewContractPage() {
  const { empresaId = '' } = useParams();
  const companies = useCompanies();
  const insurers = useInsurers();
  const create = useCreateContract();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [draft, setDraft] = useState<ContractFormState>(EMPTY_CONTRACT_FORM);

  const company = companies.data?.items.find((item) => item.id === empresaId);

  const submit = () => {
    create.mutate(
      { companyId: empresaId, contract: contractCreateBody(draft) },
      {
        onSuccess: () => {
          notify('success', ONBOARDING_COPY.contractCreated);
          void navigate(staffCompanyPath(empresaId, 'contratos'));
        },
      },
    );
  };

  if (company === undefined) {
    return (
      <div className="page">
        <AsyncSection
          state={companies}
          loadingLabel={ONBOARDING_COPY.companiesLoading}
          errorTitle={ONBOARDING_COPY.companiesError}
        >
          {() => (
            <>
              <PageHeader title={ONBOARDING_COPY.title} description={ONBOARDING_COPY.description} />
              <EmptyState
                title={ONBOARDING_COPY.companyNotFound}
                description={ONBOARDING_COPY.companyNotFoundDescription}
                action={
                  <Link className="button button--secondary" to={PATHS.staffCompanies}>
                    {ONBOARDING_COPY.back}
                  </Link>
                }
              />
            </>
          )}
        </AsyncSection>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={ONBOARDING_COPY.newContractTitle}
        description={`${company.name}. ${ONBOARDING_COPY.newContractDescription}`}
        actions={
          <Link className="button button--ghost" to={staffCompanyPath(company.id, 'contratos')}>
            {ONBOARDING_COPY.backToCompany}
          </Link>
        }
      />

      <Panel
        title={ONBOARDING_COPY.newContractTitle}
        description={ONBOARDING_COPY.newContractDescription}
      >
        <AsyncSection
          state={insurers}
          loadingLabel={ONBOARDING_COPY.insurersLoading}
          errorTitle={ONBOARDING_COPY.insurersError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={ONBOARDING_COPY.noInsurers}
          emptyDescription={ONBOARDING_COPY.noInsurersDescription}
          emptyAction={
            <Link className="button button--secondary" to={PATHS.staffInsurerNew}>
              {ONBOARDING_COPY.registerInsurer}
            </Link>
          }
        >
          {(data) => (
            <ContractForm
              insurers={data.items}
              value={draft}
              onChange={setDraft}
              onSubmit={submit}
              error={create.error}
              pending={create.isPending}
              submitLabel={ONBOARDING_COPY.createContract}
              pendingLabel={ONBOARDING_COPY.creatingContract}
              ariaLabel={ONBOARDING_COPY.newContractTitle}
              secondaryAction={
                <Link
                  className="button button--ghost"
                  to={staffCompanyPath(company.id, 'contratos')}
                >
                  {ONBOARDING_COPY.cancel}
                </Link>
              }
            />
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}
